import { useState, useCallback } from 'react';
import { rateLimiting, RateLimitResult } from '@/lib/rate-limiting';
import { useAuth } from '@/contexts/auth-context';

export interface UseRateLimitingOptions {
  action: string;
  identifier?: string;
  onRateLimitExceeded?: (result: RateLimitResult) => void;
}

export const useRateLimiting = ({ action, identifier, onRateLimitExceeded }: UseRateLimitingOptions) => {
  const { user } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [lastResult, setLastResult] = useState<RateLimitResult | null>(null);

  const checkRateLimit = useCallback(async (): Promise<RateLimitResult> => {
    setIsChecking(true);
    try {
      const result = await rateLimiting.checkRateLimit(action, user?.id, identifier);
      setLastResult(result);
      
      if (!result.allowed && onRateLimitExceeded) {
        onRateLimitExceeded(result);
      }
      
      return result;
    } finally {
      setIsChecking(false);
    }
  }, [action, user?.id, identifier, onRateLimitExceeded]);

  const recordAction = useCallback(async (): Promise<void> => {
    await rateLimiting.recordAction(action, user?.id, identifier);
  }, [action, user?.id, identifier]);

  const resetRateLimit = useCallback(async (): Promise<void> => {
    await rateLimiting.resetRateLimit(action, user?.id, identifier);
    setLastResult(null);
  }, [action, user?.id, identifier]);

  const getStatus = useCallback(async (): Promise<RateLimitResult> => {
    return await rateLimiting.getRateLimitStatus(action, user?.id, identifier);
  }, [action, user?.id, identifier]);

  return {
    checkRateLimit,
    recordAction,
    resetRateLimit,
    getStatus,
    isChecking,
    lastResult
  };
}; 